import React, { useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import Button from "../../components/utils/Button";
import Table from "../../components/utils/Table";
import CardLayout from "../../components/utils/CardLayout";
import { useDispatch, useSelector } from "react-redux";
import reportActions from "../../redux/reports/actions";
import genericActions from "../../redux/generic/actions";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";

const { getACode } = genericActions;
const { getTransactionReports, resetMessage } = reportActions;

const MDNSwapReport = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { aCodeList } = useSelector(state => state.generic);
  const [searchItem, setSearchItem] = useState("");
  const [selectedQrData, setSelectedQrData] = useState({});
  const today = new Date();
  const beforeTwoDays = new Date();
  beforeTwoDays.setDate(beforeTwoDays.getDate() - 2);
  const formatDate = (date) => date.toISOString().split("T")[0];
  const { transactionReports, transactionReportsLoading, } = useSelector((state) => state.reports)
  const { defaultCurrency } = useSelector((state) => state.auth);



  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: {
      distributorID: "",
      serialNumber: "",
      txnid: "",
      msisdn: "",
      status: "",
      fromDate: formatDate(beforeTwoDays),
      toDate: formatDate(today),
      isReporttype: 4,
    },
  });

  useEffect(() => {
    dispatch(getACode());
    dispatch(getTransactionReports({
      fromDate: formatDate(beforeTwoDays),
      toDate: formatDate(today),
      isReporttype: 4,
    }));
  }, [dispatch]);


  const statusList = [
    {
      value: "",
      label: "All Status"
    },
    {
      value: "15",
      label: "Success"
    },
    {
      value: "16",
      label: "Failed"
    }
  ];

  const columns = [
    { displayName: "New MSISDN", key: "NewMSISDN" },
    { displayName: "Old MSISDN", key: "OldMSISDN" },
    { displayName: "Serial Number", key: "SerialNumber" },
    { displayName: "Date", key: "Date" },
    { displayName: "Transaction ID", key: "TransactionId" },
    { displayName: "Status", key: "Status" },
    { displayName: "Remarks", key: "Remarks" },
  ];

  const filteredItems = useMemo(() => {
    return Array.isArray(transactionReports) && transactionReports.length > 0
      ? transactionReports.filter(item => {
        if (!searchItem) return true;
        return Object.values(item).some(val => typeof val === "string" && val.toLowerCase().includes(searchItem.toLowerCase()));
      })
      : [];
  }, [transactionReports, searchItem]);

  const rowData = useMemo(() => {
    return (filteredItems || []).map((item) => ({
      NewMSISDN: item["NewMsisdn"],
      OldMSISDN: item["OldMsisdn"],
      SerialNumber: item["SerialNumber"],
      Date: item["TransactionDate"],
      TransactionId: item["TransactionID"],
      Status: item["Status"] === "Success" ? (<span className="badge bg-success">{item["Status"]}</span>) : (<span className="badge bg-danger">{item["Status"]}</span>),
      Remarks: item["Remarks"],
    }));
  }, [filteredItems]);


  const onSubmit = (data) => {
    const payload = {
      ...data,
      distributorID: Number(data.distributorID || 0)
    }
    dispatch(getTransactionReports(payload));
  };

  const handleReset = () => {
    reset({
      distributorID: "",
      serialNumber: "",
      txnid: "",
      msisdn: "",
      status: "",
      fromDate: formatDate(beforeTwoDays),
      toDate: formatDate(today),
    });
    dispatch(resetMessage());
  };


  const handleExportExcel = () => {
    const data = filteredItems.map(item => ({
      "New MSISDN": item["NewMsisdn"],
      "Old MSISDN": item["OldMsisdn"],
      "Serial Number": item["SerialNumber"],
      "Date": item["TransactionDate"],
      "Transaction ID": item["TransactionID"],
      "Status": item["Status"],
      "Remarks": item["Remarks"],
    }));

    downloadExcelSampleFile(data, "sim-swap-report.xlsx");
  };


  return (
    <CardLayout
      title="MSISDN Swap Report"
      icon="ri-file-chart-line "
      rightContent={
        <Button
          title="Excel"
          buttonType="button"
          variant="secondary"
          onClick={handleExportExcel}
          icon={<i className="ri-download-cloud-line me-2" />}
        />}
    >
      <div>
        {/* Body */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            className="row"

          >
            {/* ACode */}
            <div className="col-lg-2 col-md-4">
              <label className="form-label fw-semibold">ACode</label>
              <select
                className="form-select"
                {...register("distributorID")}
              >
                <option value="">Select ACode</option>

                {aCodeList?.map((item) => (
                  <option
                    key={item.Id}
                    value={item.Id}
                  >
                    {item.ACode}
                  </option>
                ))}
              </select>
            </div>

            {/* Serial Number */}
            <div className="col-lg-2 col-md-4">
              <label className="form-label fw-semibold">Serial Number</label>
              <input
                type="text"
                className="form-control"
                placeholder="Serial Number"
                {...register("serialNumber")}
              />
            </div>

            {/* Transaction ID */}
            <div className="col-lg-2 col-md-4">
              <label className="form-label fw-semibold">Transaction ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="Transaction ID"
                {...register("txnid")}
              />
            </div>

            {/* Mobile Number */}
            <div className="col-lg-2 col-md-4">
              <label className="form-label fw-semibold">Mobile Number</label>
              <input
                type="text"
                className="form-control"
                placeholder="Mobile Number"
                {...register("msisdn")}
              />
            </div>

            {/* From Date */}
            <div className="col-lg-2 col-md-4">
              <label className="form-label fw-semibold">From Date</label>
              <input
                type="date"
                className="form-control"
                {...register("fromDate")}
              />
            </div>

            {/* To Date */}
            <div className="col-lg-2 col-md-4">
              <label className="form-label fw-semibold">To Date</label>
              <input
                type="date"
                className="form-control"
                {...register("toDate")}
              />
            </div>

            {/* Status */}
            <div className="col-lg-2 col-md-4">
              <label className="form-label fw-semibold">Status</label>
              <select className="form-select"
                {...register("status")}>
                {statusList.map((item, index) => (
                  <option key={index} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-3 col-md-6 d-flex align-items-end gap-3" style={{ height: '70px' }}>
              {/* Buttons */}
              <Button
                title="Get Report"
                buttonType="submit"
                variant="primary"
                disable={transactionReportsLoading}
                loading={transactionReportsLoading}
                icon={<i className="ri-search-line me-2"></i>}
                style={transactionReportsLoading ? { width: '46.5%', padding: '10px 2px' } : {}}
              />

              <Button
                title="Reset"
                buttonType="button"
                variant="secondary"
                onClick={handleReset}
                icon={<i className="ri-refresh-line me-2"></i>}
              />
            </div>
              <div className="col-lg-3 offset-lg-4 col-md-4 d-flex align-items-end" style={{ height: '70px' }}>
              <input
                type="search"
                onChange={(e) => setSearchItem(e.target.value)}
                className="form-control form-control-sm"
                placeholder="Search..."
              />
            </div>
          </div>
        </form>

        {/* Search Form end */}

      </div>
      {/* Table */}
      <div className="mt-2">
        <Table
          columns={columns}
          data={rowData}
          pagination
          loading={transactionReportsLoading}
          tableHeight="270px"
        />

      </div>
    </CardLayout>

  );
};

export default MDNSwapReport;

